/**
 * ProductCell Component
 *
 * Renders normalized technology product names using getTechnologyDisplayProduct utility.
 * Ensures consistent product formatting across Technology table and QueryBuilder.
 */

import React from 'react';

import { Technology } from '@/types';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';

export interface ProductCellProps {
  entity: Technology;
}

/**
 * Displays beautified product name
 *
 * Uses getTechnologyDisplayProduct() to:
 * - Use beautified 'name' field when available
 * - Remove vendor prefix from product name
 * - Fall back to formatted raw product
 * - Handle special cases (e.g., same vendor and product name)
 *
 * @example
 * ```typescript
 * // Raw: product="iis", name="Microsoft IIS", vendor="microsoft"
 * // Displays: "IIS"
 *
 * // Raw: product="jquery", name="jQuery", vendor="jquery"
 * // Displays: "jQuery"
 * ```
 */
export const ProductCell: React.FC<ProductCellProps> = React.memo(
  ({ entity }) => {
    const displayProduct = getTechnologyDisplayProduct(entity);

    return <span className="text-chariot-text-primary">{displayProduct}</span>;
  }
);

ProductCell.displayName = 'ProductCell';
